import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Stack } from "@mui/material";
import type { ReactNode } from "react";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { useStepNavigation } from "@/hooks/useStepNavigation";
import { primaryActionSx } from "@/theme/mui-theme";
import MobileBookingBottomSheet from "./MobileBookingBottomSheet";

interface BookingStepLayoutProps {
  children: ReactNode;
  nextButtonText?: string;
  nextLoading?: boolean;
  nextDisabled?: boolean;
  nextAction?: () => Promise<boolean>;
}

export default function BookingStepLayout({
  children,
  nextButtonText,
  nextLoading,
  nextDisabled,
  nextAction,
}: BookingStepLayoutProps) {
  const { isEmbedded } = useAppConfig();
  const { goToPreviousStep, goToNextStep, nextStep } = useStepNavigation();
  const { t } = useLingui();

  const handleNext = async () => {
    if (nextAction) {
      if (await nextAction()) {
        goToNextStep();
      }
    } else {
      goToNextStep();
    }
  };

  return (
    <Stack
      spacing={2.5}
      sx={{
        minHeight: { xs: isEmbedded ? "auto" : "calc(100dvh - 145px)", sm: "auto" },
        justifyContent: "space-between",
      }}
    >
      <Stack flex={1} px={{ xs: 1, sm: 0 }} py={{ xs: 2, sm: 0 }} spacing={2.5}>
        {children}
      </Stack>

      {/* Desktop-only nav buttons */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ display: { xs: "none", sm: "flex" } }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={goToPreviousStep}
          sx={{ bgcolor: "background.paper", color: "text.primary" }}
        >
          <Trans>Back</Trans>
        </Button>

        <Button
          variant="contained"
          size="large"
          loading={nextLoading}
          onClick={handleNext}
          disabled={nextDisabled}
          sx={nextStep === "confirmation" ? primaryActionSx : {}}
        >
          {nextButtonText || t`Next`}
        </Button>
      </Stack>

      {/* Mobile bottom sheet with nav buttons */}
      <MobileBookingBottomSheet
        nextButtonText={nextButtonText}
        nextLoading={nextLoading}
        nextDisabled={nextDisabled}
        nextAction={nextAction}
      />
    </Stack>
  );
}
